import { Controller } from "@hotwired/stimulus"
import { marked } from "marked"

const TEMPLATES = {
  email_to_edi: `## Email to EDI Processing

### Overview
Process incoming purchase orders received via email and convert them to EDI 850 transactions.

### 1. Email Intake
- Monitor designated inbox for incoming PO emails
- Identify trading partner from sender address or email content
- Extract attachments (PDF, Excel, or embedded PO)

### 2. Document Parsing
- Extract PO header information:
  - Customer PO Number
  - PO Date
  - Ship-to Address
  - Bill-to Address
  - Requested Delivery Date
- Extract line items:
  - Item Number / SKU
  - Description
  - Quantity Ordered
  - Unit of Measure
  - Unit Price

### 3. Validation Rules
- PO Number is required and must be unique
- At least one line item must be present
- All item numbers must match product catalog
- Quantities must be positive numbers
- Requested date must be at least 2 business days out

### 4. EDI Generation
- Map extracted data to EDI 850 format
- Apply trading partner-specific requirements
- Generate and transmit EDI transaction
- Send acknowledgment (EDI 855) if required

### 5. Exception Handling
- Unknown sender → Create task to identify trading partner
- Missing PO number → Flag for manual entry
- Unknown item numbers → Task to map customer item to catalog`,

  invoice_processing: `## Invoice Processing

### Overview
Extract and validate invoice data for accounts payable automation and three-way matching.

### 1. Document Validation
- Verify document is a valid invoice (not quote, statement, or duplicate)
- Check for required fields: vendor name, invoice number, date, total
- Detect and reject duplicate invoices from same vendor

### 2. Data Extraction
- **Header Information:**
  - Vendor Name and Address
  - Invoice Number
  - Invoice Date
  - Due Date / Payment Terms
  - PO Number (if referenced)
- **Line Items:**
  - Description
  - Quantity
  - Unit Price
  - Extended Amount
  - GL Code (if present)

### 3. Three-Way Match (if PO referenced)
- Match invoice to purchase order
- Verify goods/services were received
- Compare quantities and prices within tolerance:
  - Quantity: ±2% or 1 unit
  - Price: ±$0.05 per unit or 1%
  - Total: ±$10 or 0.5%

### 4. Approval Routing
- Under $500 with PO match: Auto-approve
- $500-$5,000: Department manager approval
- Over $5,000: Finance director approval
- No PO: Route to purchasing for review

### 5. Exception Handling
- Vendor not in system → Create vendor setup task
- PO not found → Route to purchasing
- Over tolerance → AP review required`,

  sales_order_entry: `## Sales Order Entry

### Overview
Process customer purchase orders and create sales orders in ERP system.

### 1. Customer Identification
- Match PO sender to customer master record
- Verify customer account is active
- Check for credit hold or account restrictions

### 2. Data Extraction
- **Order Header:**
  - Customer PO Number
  - Order Date
  - Requested Ship Date
  - Ship-to Location
  - Special Instructions
- **Line Items:**
  - Item Number / SKU / UPC
  - Description
  - Quantity Ordered
  - Unit of Measure
  - Requested Price

### 3. Validation
- All items must match valid products in catalog
- Pricing must match customer price list (±5% tolerance)
- Check inventory availability
- Minimum order requirements met

### 4. ERP Integration
- Create sales order in ERP via API
- Apply customer-specific discounts and terms
- Calculate tax and shipping
- Generate order confirmation

### 5. Exception Handling
- Unknown customer → Create prospect, task for sales
- Unknown item → Task to map to catalog
- Out of stock → Notify for backorder decision
- Price mismatch → Sales review required`,

  document_classification: `## Document Classification & Routing

### Overview
Classify incoming documents and route to appropriate processing workflows.

### 1. Document Types
- **Purchase Orders** → Order Entry workflow
- **Invoices** → Accounts Payable workflow
- **Shipping Documents** (BOL, Packing Slip) → Receiving workflow
- **Quotes / RFQs** → Sales team
- **Contracts** → Legal review
- **Remittance Advice** → Cash application

### 2. Classification Process
- Analyze document layout and structure
- Identify key markers (letterhead, titles, reference numbers)
- Extract identifying information
- Match to document type patterns

### 3. Confidence Levels
- **High (>90%):** Auto-route to workflow
- **Medium (70-90%):** Route with verification flag
- **Low (<70%):** Manual classification required

### 4. Routing Rules
- POs with known customer → Sales Order Entry
- Invoices with PO → Three-Way Match
- Invoices without PO → Manual AP Review
- Shipping docs → Match to open orders

### 5. Exception Handling
- Unclassified documents → Review queue
- Multi-page mixed documents → Split and reclassify`,

  ap_automation: `## Accounts Payable Automation

### Overview
End-to-end AP processing from invoice receipt to payment.

### 1. Invoice Capture
- Receive invoices via email, portal, or scan
- Extract all invoice data automatically
- Match vendor to existing records

### 2. Coding & Allocation
- Apply GL coding based on:
  - Vendor defaults
  - PO line item codes
  - Historical patterns
- Split across cost centers if needed

### 3. Approval Workflow
| Amount | Approver |
|--------|----------|
| < $500 | Auto-approve (if PO matched) |
| $500 - $2,500 | Department Manager |
| $2,500 - $10,000 | Director |
| > $10,000 | VP / CFO |

### 4. Payment Processing
- Apply payment terms and early pay discounts
- Schedule payment based on due date
- Batch payments by vendor and method

### 5. Exception Handling
- Missing approvals → Escalation after 3 days
- Disputed invoices → Vendor communication task
- Duplicate detection → Auto-reject and notify`,

  custom_blank: `## [Your Process Name]

### Overview
Describe what this agent does and its purpose.

### 1. First Step
- Detail the first step in your process
- Include specific actions to take
- Note any conditions or checks

### 2. Second Step
- Continue with next steps
- Be specific about data to extract
- Include validation requirements

### 3. Validation Rules
- List your validation rules here
- Include acceptable tolerances
- Define required vs optional fields

### 4. Integration / Output
- Describe what happens with processed data
- API calls, file generation, notifications

### 5. Exception Handling
- How to handle errors and edge cases
- Escalation paths
- Manual review triggers`
}

export default class extends Controller {
  static targets = ["templatesPanel"]

  apply(event) {
    event.preventDefault()
    const templateKey = event.currentTarget.dataset.template
    const template = TEMPLATES[templateKey]
    
    if (!template) return

    const editorWrapper = document.querySelector('[data-controller="markdown-editor"]')
    const input = editorWrapper?.querySelector('[data-markdown-editor-target="input"]')
    if (!input) return
    if (input.value.trim() && !confirm("This will replace your current SOP. Continue?")) return

    const markdownController = this.application.getControllerForElementAndIdentifier(editorWrapper, 'markdown-editor')
    if (markdownController) {
      markdownController.setContent(template)
    } else {
      // The named textarea remains usable while the rich editor is loading.
      input.value = template
      input.dispatchEvent(new Event('input', { bubbles: true }))
    }
    if (this.hasTemplatesPanelTarget) this.templatesPanelTarget.classList.add('hidden')
  }

  showTemplates() {
    if (this.hasTemplatesPanelTarget) {
      this.templatesPanelTarget.classList.remove('hidden')
    }
  }
}
